/**
 * Theme.js — Global utilities and Alpine.js cart store
 *
 * 1. Shopify.formatMoney — single canonical money formatter
 * 2. Alpine.store('cart') — reactive cart state shared across all components
 * 3. window.addToCart — backward-compat shim delegating to cart store
 */

// 1. Global Shopify.formatMoney
(function () {
  window.Shopify = window.Shopify || {};

  // Read money format from the JSON tag injected by theme.liquid
  var dataEl = document.getElementById('shopify-data');
  var moneyFormat = '${{amount}}';
  if (dataEl) {
    try {
      moneyFormat = JSON.parse(dataEl.textContent).moneyFormat || moneyFormat;
    } catch (e) { /* use default */ }
  }
  Shopify.moneyFormat = moneyFormat;

  function formatWithDelimiters(number, precision, thousands, decimal) {
    thousands = thousands || ',';
    decimal = decimal || '.';
    if (isNaN(number) || number === null || number === undefined) return '0.00';
    number = (number / 100.0).toFixed(precision);
    var parts = number.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    var cents = parts[1] ? decimal + parts[1] : '';
    return dollars + cents;
  }

  Shopify.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    var fmt = format || Shopify.moneyFormat;
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var match = fmt.match(placeholderRegex);
    if (!match || !match[1]) return '$' + (cents / 100).toFixed(2);

    var value = '';
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2); break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' '); break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'"); break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return fmt.replace(placeholderRegex, value);
  };
})();

// 2. Alpine cart store — registered on alpine:init
document.addEventListener('alpine:init', function () {
  Alpine.store('cart', {
    items: [],
    count: 0,
    totalPrice: 0,
    note: '',
    error: '',
    loading: false,

    async refresh() {
      try {
        var response = await fetch('/cart.js');
        if (!response.ok) throw new Error('HTTP ' + response.status);
        var cart = await response.json();
        this.items = cart.items || [];
        this.count = cart.item_count || 0;
        this.totalPrice = cart.total_price || 0;
        this.note = cart.note || '';
      } catch (e) {
        console.error('Error fetching cart:', e);
      }
    },

    async addItem(variantId, quantity, properties) {
      this.error = '';
      this.loading = true;
      try {
        var body = { id: variantId, quantity: quantity || 1 };
        if (properties && properties.selling_plan) {
          body.selling_plan = properties.selling_plan;
        }
        var response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
        if (!response.ok) {
          var errorData = await response.json().catch(function () { return {}; });
          this.error = errorData.message || errorData.description || 'Unable to add item to cart.';
          await this.refresh();
          this.loading = false;
          return null;
        }
        var data = await response.json();
        await this.refresh();
        this.loading = false;
        return data;
      } catch (e) {
        console.error('Error adding to cart:', e);
        this.error = 'Unable to add item to cart. Please try again.';
        this.loading = false;
        return null;
      }
    },

    async updateItem(key, quantity) {
      this.error = '';
      this.loading = true;
      try {
        var response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: quantity })
        });
        if (!response.ok) {
          var errorData = await response.json().catch(function () { return {}; });
          this.error = errorData.message || errorData.description || 'Unable to update quantity.';
        }
        await this.refresh();
      } catch (e) {
        console.error('Error updating cart:', e);
        this.error = 'Unable to update quantity. Please try again.';
      }
      this.loading = false;
    },

    async removeItem(key) {
      await this.updateItem(key, 0);
    }
  });

  // Initialize cart data on page load
  Alpine.store('cart').refresh();
});

// 3. Backward-compat shim for window.addToCart
window.addToCart = async function (variantId, quantity, properties) {
  // Wait for Alpine store to be available
  if (typeof Alpine === 'undefined' || !Alpine.store('cart')) {
    console.error('Alpine cart store not ready');
    return;
  }
  var store = Alpine.store('cart');
  var data = await store.addItem(variantId, quantity || 1, properties || {});

  // Check cart type setting
  var cartType = document.documentElement.getAttribute('data-cart-type') || 'drawer';
  if (cartType === 'page') {
    window.location.href = '/cart';
  } else {
    window.dispatchEvent(new CustomEvent('cart-drawer-open'));
  }

  return data;
};
